import { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack, StackItem, ProgressIndicator, Container } from '@optimus-web/core';
import { Restriction, RestrictionSplicingResult } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { clearRestrictions, navigateToRestrictionIndexPage, replaceRestriction, setRestriction } from '../../actions';
import { RestrictionFormStep } from '../../utils/enums';
import { ServiceIdContext } from '../contexts';
import { RESTRICTIONS_CALENDAR_QUERY_KEY } from '../../queries';
import { RestrictionFormContainer } from './initialize';
import { RestrictionSplicingPreview } from './preview';
import {
    DeleteFormValues,
    EditFormValues,
    MultiConditionsCreateFormValues,
    MultiStepFormProps,
    PreviewData,
} from './types';

const previewDataInitialState = {
    create: [],
    delete: [],
    keep: [],
    invalid: [],
};

const firstStepLabelMap = {
    createForm: TextKey.CreateRestrictions,
    editForm: TextKey.EditRestriction,
    deleteForm: TextKey.DeleteRestrictions,
};

const createPreviewAction = (action: 'create' | 'delete' | 'keep' | 'invalid') =>
    array.map(
        (restriction: Restriction) =>
            ({
                action,
                ...restriction,
            }) as const
    );

export const MultistepPreviewForm = (props: MultiStepFormProps) => {
    const dispatch = useCommanderThunkDispatch();

    const serviceId = useContext(ServiceIdContext);
    const queryClient = useQueryClient();

    const [createFormValues, setCreateFormValues] = useState<MultiConditionsCreateFormValues>();
    const [editFormValues, setEditFormValues] = useState<EditFormValues>();
    const [deleteFormValues, setDeleteFormValues] = useState<DeleteFormValues>();

    const [previewData, setPreviewData] = useState<PreviewData>(previewDataInitialState);
    const [currentStep, setCurrentStep] = useState<RestrictionFormStep>(RestrictionFormStep.FormStep);

    const handleSaveChanges = async () => {
        if (props.formType === 'createForm' && createFormValues) {
            await dispatch(setRestriction({ formValues: createFormValues, serviceId, preview: false }));
            showSuccessAlert({ description: lt('RestrictionsHaveBeenCreated') });
            dispatch(navigateToRestrictionIndexPage(serviceId));
        } else if (props.formType === 'editForm' && editFormValues) {
            await dispatch(
                replaceRestriction({ formValues: editFormValues, serviceId, id: editFormValues.id, preview: false })
            );
            showSuccessAlert({ description: lt('RestrictionHasBeenEdited') });
            dispatch(navigateToRestrictionIndexPage(serviceId));
        } else if (props.formType === 'deleteForm' && deleteFormValues) {
            await dispatch(clearRestrictions(serviceId, deleteFormValues, false));
            showSuccessAlert({ description: lt('RestrictionsHaveBeenDeleted') });
            dispatch(navigateToRestrictionIndexPage(serviceId));
        }
        await queryClient.invalidateQueries({ queryKey: RESTRICTIONS_CALENDAR_QUERY_KEY });
    };

    let formComponent;
    let currentFormValues;

    const setPreviewState = (
        result: Pick<
            RestrictionSplicingResult,
            'restrictionsToAdd' | 'restrictionsToDelete' | 'restrictionsToKeep' | 'invalidRestrictions'
        >
    ) => {
        setPreviewData({
            create: pipe(result.restrictionsToAdd, createPreviewAction('create')),
            delete: pipe(result.restrictionsToDelete, createPreviewAction('delete')),
            keep: pipe(result.restrictionsToKeep, createPreviewAction('keep')),
            invalid: pipe(result.invalidRestrictions, createPreviewAction('invalid')),
        } as PreviewData);
        setCurrentStep(RestrictionFormStep.PreviewStep);
    };

    switch (props.formType) {
        case 'createForm': {
            formComponent = (
                <RestrictionFormContainer
                    {...props}
                    formType={props.formType}
                    formValues={createFormValues}
                    handleSubmit={async (formValues) => {
                        const { value } = await dispatch(setRestriction({ formValues, serviceId, preview: true }));
                        setCreateFormValues(formValues);
                        setPreviewState(value);
                    }}
                    multistep
                />
            );
            currentFormValues = createFormValues;
            break;
        }
        case 'editForm': {
            formComponent = (
                <RestrictionFormContainer
                    {...props}
                    formType={props.formType}
                    formValues={editFormValues}
                    handleSubmit={async (formValues) => {
                        const { value } = await dispatch(
                            replaceRestriction({
                                formValues,
                                serviceId,
                                id: formValues.id,
                                preview: true,
                            })
                        );
                        setEditFormValues(formValues);
                        setPreviewState(value);
                    }}
                    multistep
                />
            );
            currentFormValues = editFormValues;
            break;
        }
        case 'deleteForm': {
            formComponent = (
                <RestrictionFormContainer
                    {...props}
                    formType={props.formType}
                    formValues={deleteFormValues}
                    handleSubmit={async (formValues) => {
                        const { value } = await dispatch(clearRestrictions(serviceId, formValues, true));
                        setDeleteFormValues(formValues);
                        setPreviewState(value);
                    }}
                    multistep
                />
            );
            currentFormValues = deleteFormValues;
            break;
        }
    }

    const stepMap = {
        [RestrictionFormStep.FormStep]: formComponent,
        [RestrictionFormStep.PreviewStep]: (
            <Container maxWidth="960px" width="100%" padding="100">
                <RestrictionSplicingPreview
                    formType={props.formType}
                    formValues={currentFormValues}
                    serviceId={serviceId}
                    previewData={previewData}
                    handleGoBack={() => setCurrentStep(RestrictionFormStep.FormStep)}
                    handleSaveChanges={handleSaveChanges}
                />
            </Container>
        ),
    };

    const steps = [
        {
            value: RestrictionFormStep.FormStep,
            label: <LocalizedText textKey={firstStepLabelMap[props.formType]} />,
            completed: currentStep !== RestrictionFormStep.FormStep,
            editable: true,
        },
        {
            value: RestrictionFormStep.PreviewStep,
            label: <LocalizedText textKey={TextKey.ReviewChanges} />,
            completed: false,
            editable: currentStep === RestrictionFormStep.PreviewStep,
        },
    ];

    return (
        <Stack vertical alignment="center" itemAlignment="center" spacing="200">
            <StackItem padding="100" minWidth="30%">
                <ProgressIndicator
                    steps={steps}
                    activeStep={currentStep}
                    onStepChange={(step) => {
                        setPreviewData(previewDataInitialState);
                        setCurrentStep(step as RestrictionFormStep.FormStep);
                    }}
                />
            </StackItem>
            {stepMap[currentStep]}
        </Stack>
    );
};
